import React, { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Formik, FormikErrors, FormikHelpers } from "formik";
import * as yup from "yup";

import {
  HvButton,
  HvDialog,
  HvDialogTitle,
  HvDialogContent,
  HvDialogActions,
  HvBanner,
  HvInput,
  HvTextArea,
} from "@hitachivantara/uikit-react-core";

import Loading from "components/layout/Loading";

import useStyles from "./styles";

export interface CategoryFormData {
  name: string;
  description: string;
}

export type CategoryFormMode = "add" | "edit";
export type CategoryFormType = "category" | "subcategory";

interface FormStatus {
  serverSideError?: string;
  serverSideErrors?: FormikErrors<CategoryFormData>;
}

interface DialogProps {
  isOpen: boolean;
  onSave: (data: CategoryFormData) => Promise<unknown>;
  onClose: (success: boolean, message?: string) => void;
  type: CategoryFormType;
  mode: CategoryFormMode;
  initialValues?: CategoryFormData;
}

/**
 * Dialog form for editing a Category.
 *
 * While the use cases remain similar, it is shared with the edit of Subcategory,
 * by switching the type paramenter.
 *
 * @param isOpen If `true`, the Dialog is open.
 * @param onSave Called for saving the category.
 * @param onClose Called when the dialog is closed.
 *                The success parameter indicates if the category was saved, the message if something should be shown to the user.
 * @param type Indicates if the controlling subject is Category or Subcategory.
 *             Mainly used for i18n, as it is the various callbacks that deal with the data.
 * @param mode Indicates if adding a new category or editing a existing one.
 *             Mainly used for i18n, as it is the various callbacks that deal with the data.
 * @param initialValues The initial field values.
 */
const AddEditCategoryDialog: React.FC<DialogProps> = ({
  isOpen,
  onSave,
  onClose,
  type,
  mode,
  initialValues: initialValuesProp,
}) => {
  const classes = useStyles();

  const { t } = useTranslation("addEditCategoryDialog");

  const subject = t(`subject.${type}`);

  const onCloseHandler = (
    event: unknown,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason !== "backdropClick") {
      onClose?.(false);
    }
  };

  const onCancelHandler = () => {
    onClose?.(false);
  };

  const onSubmitHandler = async (
    data: CategoryFormData,
    actions: FormikHelpers<CategoryFormData>
  ) => {
    try {
      await onSave({
        name: data.name.trim(),
        description: data.description.trim(),
      });
      onClose?.(true, t(`validation.success.${mode}`, { subject }));
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;

      if (errorCode == null) {
        actions.setStatus({
          serverSideError: t(`validation.serverUnreachable.${mode}`, {
            subject,
          }),
        });
      } else if (errorCode.endsWith("_name_already_taken")) {
        actions.setStatus({
          serverSideErrors: {
            name: t("validation.nameAlreadyTaken", { subject }),
          },
        });
      } else if (
        errorCode.endsWith("_args_required") ||
        (errorCode.endsWith("_not_found") && !errorCode.startsWith("child_"))
      ) {
        actions.setStatus({
          serverSideError: t("validation.notFound", { subject }),
        });
      } else {
        actions.setStatus({
          serverSideError: t(`validation.unknownError.${mode}`, {
            subject,
          }),
        });
      }
    }
  };

  const nameInputRef = useRef<HTMLInputElement>();
  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const validationSchema = useMemo(() => {
    return yup.object({
      name: yup.string().required(t("validation.nameRequired", { subject })),
      description: yup.string(),
    });
  }, [t, subject]);

  const initialValues: CategoryFormData = {
    name: initialValuesProp?.name ?? "",
    description: initialValuesProp?.description ?? "",
  };
  const initialStatus: FormStatus = { serverSideErrors: {} };

  return (
    <HvDialog
      buttonTitle={t("buttons.close", { subject })}
      classes={{ paper: classes.paper }}
      open={isOpen}
      onClose={onCloseHandler}
    >
      <Formik
        initialStatus={initialStatus}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
      >
        {(props) => {
          const {
            values,
            errors,
            status: { serverSideError, serverSideErrors = {} },
            touched,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            setStatus,
            isSubmitting,
            isValid,
          } = props;

          const clearServerSideErrors = () => {
            setStatus({});
          };

          const parseStatus = (field: keyof CategoryFormData) =>
            serverSideErrors[field] || (errors[field] && touched[field])
              ? "invalid"
              : "valid";

          const parseStatusMessage = (field: keyof CategoryFormData) => {
            if (serverSideErrors[field]) {
              return serverSideErrors[field];
            }

            return errors[field] && touched[field] ? errors[field] : "";
          };

          return (
            <form onSubmit={handleSubmit} noValidate>
              <HvDialogTitle className={classes.dialogTitle}>
                {t(`title.${mode}`, { subject })}
              </HvDialogTitle>
              <HvDialogContent>
                <HvBanner
                  className={classes.errorBanner}
                  style={{ top: undefined }}
                  open={serverSideError != null}
                  variant="error"
                  showIcon
                  label={serverSideError}
                  onClose={clearServerSideErrors}
                />
                <HvInput
                  inputRef={nameInputRef}
                  className={classes.input}
                  inputProps={{
                    autoComplete: "off",
                  }}
                  name="name"
                  required
                  label={t("name.label", { subject })}
                  placeholder={t("name.placeholder", { subject })}
                  labels={{
                    clearButtonLabel: t("name.clearButtonLabel", {
                      subject,
                    }),
                  }}
                  value={values.name}
                  status={parseStatus("name")}
                  statusMessage={parseStatusMessage("name")}
                  onChange={(evt, value) => {
                    setFieldTouched("name");
                    setFieldValue("name", value);
                    clearServerSideErrors();
                  }}
                />
                <HvTextArea
                  classes={{ input: classes.textareaInput }}
                  name="description"
                  label={t("description.label", { subject })}
                  placeholder={t("description.placeholder", {
                    subject,
                  })}
                  rows={3}
                  value={values.description}
                  status={parseStatus("description")}
                  statusMessage={parseStatusMessage("description")}
                  onChange={(evt, value) => {
                    setFieldTouched("description");
                    setFieldValue("description", value);
                    clearServerSideErrors();
                  }}
                />
              </HvDialogContent>
              {isSubmitting && (
                <Loading
                  delay={500}
                  loadingLabel={t("submitting", { subject })}
                  className={classes.loading}
                />
              )}
              <HvDialogActions>
                <HvButton
                  category="ghost"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    values.name === "" ||
                    (values.name.trim() === initialValues.name &&
                      values.description.trim() === initialValues.description)
                  }
                >
                  {t("buttons.save", { subject })}
                </HvButton>
                <HvButton
                  category="ghost"
                  onClick={onCancelHandler}
                  disabled={isSubmitting}
                >
                  {t("buttons.cancel", { subject })}
                </HvButton>
              </HvDialogActions>
            </form>
          );
        }}
      </Formik>
    </HvDialog>
  );
};

export default AddEditCategoryDialog;
